import styles from "./FilterRangeSlider.module.scss";
import "rc-slider/assets/index.css";
import { h, FunctionalComponent, Fragment, JSX } from "preact";
import { useState, useEffect } from "preact/hooks";
import Slider from "rc-slider";
import { FilterItemProps } from "components/Filters/Filters";
import { Text } from "preact-i18n";

export interface FilterRangeSliderProps extends FilterItemProps<
    number | number[]
> {
    label?: string;
    isSingle?: boolean;
    minValue?: number;
    maxValue?: number;
    hasInputs?: boolean;
    customValues?: Array<
        | number
        | {
              value: number;
              label?: string;
              labelId?: string;
          }
    >;
    step?: number;
}

const FilterRangeSlider: FunctionalComponent<FilterRangeSliderProps> = ({
    label = "",
    isSingle = false,
    minValue = 10,
    maxValue = 2000,
    hasInputs = true,
    value,
    onChange,
    customValues,
    step,
}: FilterRangeSliderProps) => {
    const [singleValue, setSingleValue] = useState(
        typeof value === "number" ? value || 300 : 300,
    );
    const [multiValue, setMultiValue] = useState(
        Array.isArray(value) ? value : [300, 500],
    );

    useEffect(() => {
        if (typeof value === "number") setSingleValue(value || 300);
        else if (Array.isArray(value)) setMultiValue(value);
    }, [value]);

    const updateSliderValue = (val: number | number[]) => {
        if (isSingle) {
            if (typeof val === "number") setSingleValue(val);
        } else {
            if (Array.isArray(val)) setMultiValue(val);
        }
    };

    const commitSliderValue = (val: number | number[]) => {
        if (!onChange) {
            return;
        }
        onChange(val);
    };

    const getLabel = (
        val: NonNullable<FilterRangeSliderProps["customValues"]>[number],
    ) => {
        if (typeof val === "number") {
            return `${val}`;
        }
        if (val?.labelId !== undefined) {
            return <Text id={val.labelId}>{val.label}</Text>;
        }
        return val.label;
    };

    const setMarks = (minValue: number, maxValue: number) => {
        if (customValues) {
            return customValues.reduce(
                (
                    acc: Record<
                        number,
                        {
                            style: JSX.CSSProperties;
                            label: import("preact").ComponentChildren;
                        }
                    >,
                    val,
                    index,
                ) => {
                    const value = typeof val === "number" ? val : val.value;
                    const label = getLabel(val);
                    const isFirst = index === 0;
                    const isLast = index === customValues.length - 1;
                    const markerStyle: JSX.CSSProperties = isFirst
                        ? { textAlign: "left", transform: "translateX(-10%)" }
                        : isLast
                          ? {
                                textAlign: "right",
                                transform: "translateX(-85%)",
                            }
                          : {
                                textAlign: "center",
                                transform: "translateX(-50%)",
                            };

                    acc[value] = {
                        style: markerStyle,
                        label,
                    };
                    return acc;
                },
                {} as Record<
                    number,
                    {
                        style: JSX.CSSProperties;
                        label: import("preact").ComponentChildren;
                    }
                >,
            );
        }
        return {
            [minValue]: {
                style: { textAlign: "left", transform: "translateX(-35%)" },
                label: `${minValue}`,
            },
            [maxValue]: {
                style: { textAlign: "right", transform: "translateX(-85%)" },
                label: `${maxValue}`,
            },
        };
    };

    const sliderProps = {
        min: minValue,
        max: maxValue,
        step: step || 1,
        keyboard: false,
        marks: setMarks(minValue, maxValue),
        trackStyle: { border: "solid 2px #14456B" },
        handleStyle: {
            backgroundColor: "#14456B",
            border: "solid 2px #14456B",
            width: "16px",
            height: "16px",
            opacity: 1,
        },
        activeDotStyle: {
            borderColor: "#14456B",
        },
        allowCross: false,
        value: isSingle ? singleValue : multiValue,
        range: !isSingle,
        onChange: updateSliderValue,
        onAfterChange: commitSliderValue,
    };

    const handleInputChange = (e: Event) => {
        let val = Number((e.target as HTMLInputElement).value);
        if (val < minValue) {
            val = minValue;
        }
        if (val > maxValue) {
            val = maxValue;
        }
        setSingleValue(val);
        commitSliderValue(val);
    };

    const handleKeyDown = (
        event: JSX.TargetedKeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === "Enter" || event.key === "Tab") {
            // 👇 Get input value
            handleInputChange(event);
        }
    };

    return (
        <div className={styles.mvRangeSliderWrapper}>
            <div className={styles.mvRangeSliderTop}>
                <span className={styles.mvRangeSliderLabel}>{label}</span>
                {hasInputs && (
                    <div className={styles.mvRangeSliderInputs}>
                        {isSingle && (
                            <input
                                type="number"
                                name="singleValue"
                                min={minValue}
                                max={maxValue}
                                onBlur={handleInputChange}
                                onKeyDown={handleKeyDown}
                                value={singleValue}
                            />
                        )}
                        {!isSingle && (
                            <Fragment>
                                <input
                                    type="number"
                                    name="minValue"
                                    min={minValue}
                                    max={maxValue}
                                    onChange={(e) => {
                                        setMultiValue(
                                            (currentVal: number[]) => [
                                                Number(
                                                    (
                                                        e.target as HTMLInputElement
                                                    ).value,
                                                ),
                                                currentVal[1],
                                            ],
                                        );
                                    }}
                                    value={multiValue[0]}
                                />
                                <span> - </span>
                                <input
                                    type="number"
                                    name="maxValue"
                                    min={minValue}
                                    max={maxValue}
                                    value={multiValue[1]}
                                    onChange={(e) => {
                                        setMultiValue(
                                            (currentVal: number[]) => [
                                                currentVal[0],
                                                Number(
                                                    (
                                                        e.target as HTMLInputElement
                                                    ).value,
                                                ),
                                            ],
                                        );
                                    }}
                                />
                            </Fragment>
                        )}
                    </div>
                )}
            </div>

            <Slider {...sliderProps} />
        </div>
    );
};

export default FilterRangeSlider;
